import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid2,
  Alert,
  Tooltip,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListItem,
  List,
  Paper,
} from "@mui/material";
import { Message } from "../../../types/Message";
import { MessagePriority } from "../../../data/Enums";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import useApi from "../../../hooks/useApi";
import { downloadAttachment, getInternalReplyMessages } from "../../../services/messageService";
import { format } from "date-fns";
import {
  ENTITYTYPE,
  GLOBALSETTINGS,
  ModuleNames,
  SEVERITIES,
  COMMONCONSTANTS,
} from "../../../data/constants";
import MessageDialog from "./MessageDialog";
import { useSendMessage } from "../../../hooks/useSendMessage";
import BackdropProgressBar from "../../../components/ui/BackdropProgressBar";
import { useToaster } from "../../../hooks/useToaster";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import {
  DOWNLOADVALIDATIONS,
  INTERNALMESSAGES,
  MESSAGE_FAIL,
  MESSAGE_SENT,
} from "../../../constants/messages";
import SendWithScheduleButton from "../../../components/ui/SendWithScheduleButton";
import { getFileExtension, isEmptyMessage, renderSvgIcon } from "../../../utils/commonHelper";
import MarkAsUnreadIcon from "@mui/icons-material/MarkAsUnread";
import "./Messages.scss";
import { formatFileSize } from "../../../utils/messageHelper";
import QuillWithDrop, { QuillWithDropRef } from "./QuillWithDrop";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useUser } from "../../../context/UserContext";
import { useMenu } from "../../../context/MenuContext";

interface ReplyMessageProps {
  conversationId: number | null;
  messageId: number;
  mainSubject: string;
  open: boolean;
  onClose: () => void;
  toUserId: number;
  toUserName: string;
  type: string;
}

const ReplyMessage: React.FC<ReplyMessageProps> = ({
  conversationId,
  messageId,
  mainSubject,
  open,
  onClose,
  toUserId,
  toUserName,
  type,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [priority, setPriority] = useState<MessagePriority>(MessagePriority.Normal);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const lastAccordionRef = useRef<HTMLDivElement | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { loading, executeApi } = useApi();
  const [messageBody, setMessageBody] = useState("");
  const [displayNew, setDisplayNew] = useState(false);
  const { sendMessage } = useSendMessage();
  const showToaster = useToaster();
  const quillRef = useRef<QuillWithDropRef>(null);
  const [sendMessageLoading, setSendMessageLoading] = useState<boolean>(false);
  const { userId, userName } = useUser();
  const [replyToUserIds, setReplyToUserIds] = useState<number[]>(toUserId ? [toUserId] : []);
  const [replyToCacheNames, setReplyToCacheNames] = useState<string[]>(
    toUserName ? [toUserName] : [],
  );
  const [canEdit, setCanEdit] = useState(false);
  const { permissions } = useMenu();

  useEffect(() => {
    if (permissions && permissions.length > 0) {
      const isEdit = permissions?.find(
        (permission) => permission.moduleName === ModuleNames.Messages,
      )?.canEdit;
      setCanEdit(isEdit);
    }
  }, [permissions]);

  const handleToggleMessage = (id: number, expanded: boolean) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (expanded) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleClose = () => {
    setDisplayNew(false);
    setValidationError(null);
    setMessageBody("");
    setExpandedIds(new Set());
    setMessages([]);
    setMessagesLoaded(false);
    setPriority(MessagePriority.Normal);
    onClose();
  };

  const fetchReplyMessages = async () => {
    // From Inbox: filter to only show messages involving the specific sender (toUserId)
    // From Sent: show all messages (no filter)
    const filterUserId = type === ENTITYTYPE.SENT ? null : toUserId;
    // Pass messageId so the SP only returns messages up to and including the clicked one
    const upToMsgId = messageId;

    let replyMessages = await executeApi<
      [number | null, number | null, number | null],
      Message[]
    >(
      (args) => getInternalReplyMessages(args[0], args[1], args[2]),
      [conversationId, filterUserId, upToMsgId],
    );

    // Stage 1: if the user-pair filter excluded results, retry without it.
    if (Array.isArray(replyMessages) && replyMessages.length === 0 && filterUserId !== null) {
      replyMessages = await executeApi<
        [number | null, number | null, number | null],
        Message[]
      >(
        (args) => getInternalReplyMessages(args[0], args[1], args[2]),
        [conversationId, null, upToMsgId],
      );
    }

    if (replyMessages) {
      setMessages(replyMessages);
      setExpandedIds(new Set(replyMessages.map((msg) => msg.messageId)));

      // Determine reply target based on tab.
      if (type === ENTITYTYPE.SENT) {
        // Sent tab: only the clicked message is returned.
        // Reply targets = that message's To recipients only (CC excluded).
        const clickedMsg = replyMessages[0];
        if (clickedMsg) {
          const ids = clickedMsg.allToUserIds
            ? clickedMsg.allToUserIds
                .split(",")
                .map((id) => Number(id.trim()))
                .filter((id) => !isNaN(id) && id > 0)
            : clickedMsg.toUserIds ?? (clickedMsg.toUserId ? [clickedMsg.toUserId] : []);
          const names = clickedMsg.toCacheName
            ? clickedMsg.toCacheName.split(", ").filter((name) => name.length > 0)
            : clickedMsg.toCacheNames ?? [];
          setReplyToUserIds(ids);
          setReplyToCacheNames(names);
        }
      } else {
        // Inbox tab: reply only to the person who sent the message
        const lastOtherMsg = [...replyMessages].reverse().find((m) => m.fromUserId !== userId);
        if (lastOtherMsg) {
          setReplyToUserIds([lastOtherMsg.fromUserId]);
          setReplyToCacheNames([lastOtherMsg.cacheFromName ?? ""]);
        }
        // If no other user found, keep initial prop values (toUserId/toUserName)
      }

      setMessagesLoaded(true);
    }
  };

  useEffect(() => {
    if (open) {
      fetchReplyMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && messagesLoaded && lastAccordionRef.current) {
      lastAccordionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [open, messagesLoaded]);

  const validate = () => {
    if (isEmptyMessage(messageBody)) {
      setValidationError(INTERNALMESSAGES.BODY_REQUIRED);
      return false;
    }
    return true;
  };

  const handleSend = async (scheduleDate: Date | null) => {
    if (!validate()) return;
    setSendMessageLoading(true);

    const files = quillRef.current?.getAttachments() ?? [];

    // Determine the correct replyToMessageId:
    // - From Inbox tab: reply to the last message from the OTHER user
    //   so backend Case 1 (recipient replying) sends only to original sender
    // - From Sent tab: reply to the original message (first in thread)
    //   so backend Case 2 (sender replying to own original) sends to all recipients
    let replyToMsgId: number;
    if (type === ENTITYTYPE.SENT) {
      // Sent tab: only the clicked message is shown, reply to it
      replyToMsgId = messageId;
    } else {
      // For Inbox tab: reply to the last message from the OTHER user
      // This ensures backend Case 1 fires (recipient replying ? send to original sender)
      const lastOtherMsg = [...messages].reverse().find((m) => m.fromUserId !== userId);
      replyToMsgId = lastOtherMsg
        ? lastOtherMsg.messageId
        : messages[messages.length - 1].messageId;
    }

    const success = await sendMessage(
      userId,
      userName,
      replyToUserIds,
      replyToCacheNames,
      replyToUserIds.map(() => COMMONCONSTANTS.TO),
      mainSubject,
      messageBody,
      false,
      priority,
      scheduleDate,
      files,
      conversationId,
      replyToMsgId,
    );

    const recipientDisplay =
      replyToCacheNames.length > 0 ? replyToCacheNames.join(", ") : toUserName;

    setSendMessageLoading(false);
    if (success) {
      showToaster(MESSAGE_SENT(), SEVERITIES.SUCCESS);
      handleClose();
    } else {
      showToaster(MESSAGE_FAIL(recipientDisplay), SEVERITIES.ERROR);
    }
  };

  const handleMessageChange = (content: string) => {
    setMessageBody(content);
    const textOnly = content.replace(/<[^>]+>/g, "").trim();
    if (textOnly.length > 0) {
      setValidationError(null);
    }
  };

  const downloadFile = async (attachmentId: number, fileName: string) => {
    const blob = await executeApi<number, Blob>(downloadAttachment, attachmentId);

    if (!blob) {
      showToaster(DOWNLOADVALIDATIONS.DOWNLOAD_ERROR, SEVERITIES.ERROR);
      return;
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleReplyClick = () => {
    setDisplayNew(true);
    setTimeout(() => {
      if (quillRef.current) {
        quillRef.current.getEditor().root.scrollIntoView({ behavior: "smooth", block: "start" });
        quillRef.current.focus();
      }
    }, 0);
  };

  return (
    <MessageDialog
      open={open}
      onClose={handleClose}
      title={
        <Box className="msg-dialog-title-row">
          <MarkAsUnreadIcon sx={{ color: "white", marginRight: 1, flexShrink: 0 }} />
          <Box component="span" sx={{ flexShrink: 0 }}>
            Subject:&nbsp;
          </Box>
          <Tooltip title={mainSubject} arrow>
            <Box component="span" className="msg-dialog-title-text">
              {mainSubject}
            </Box>
          </Tooltip>
        </Box>
      }
      dialogActions={
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box>
              {displayNew && (
                <Box display="flex" gap={1}>
                  <Select
                    size="small"
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value) as MessagePriority)}
                    sx={{ minWidth: 120 }}
                  >
                    {Object.entries(MessagePriority)
                      .filter(([, value]) => !isNaN(Number(value)))
                      .map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {key}
                        </MenuItem>
                      ))}
                  </Select>
                  <Tooltip title="Attach Files" arrow>
                    <IconButton
                      component="label"
                      size="small"
                      sx={{
                        color: "primary.main",
                        border: "1px solid",
                        borderColor: "primary.main",
                        borderRadius: "6px",
                        padding: "6px",
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.04)",
                        },
                      }}
                    >
                      <AttachFileIcon />
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={(e) => {
                          if (!e.target.files) return;
                          const files = Array.from(e.target.files);
                          quillRef.current?.handleManualAttach?.(files);
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            <Box display="flex" gap={1}>
              {displayNew ? (
                <>
                  <Button onClick={handleClose} size="small">
                    Cancel
                  </Button>
                  <SendWithScheduleButton
                    onSend={() => handleSend(null)}
                    onScheduleSend={(date) => handleSend(date)}
                  />
                </>
              ) : (
                canEdit && (
                  <Button
                    id="reply"
                    variant="contained"
                    color="primary"
                    onClick={handleReplyClick}
                    size="small"
                    startIcon={<ReplyOutlinedIcon />}
                  >
                    Reply
                  </Button>
                )
              )}
            </Box>
          </Box>
        </>
      }
    >
      <>
        {<BackdropProgressBar loading={loading || sendMessageLoading} />}
        {messagesLoaded &&
          messages.map((item, index) => {
            const isExpanded = expandedIds.has(item.messageId);
            const isLast = index === messages.length - 1;
            return (
              <div key={`msg-${item.messageId}-${index}`} ref={isLast ? lastAccordionRef : null}>
                <Accordion
                  expanded={isExpanded}
                  disableGutters
                  sx={{
                    my: 0.5,
                  }}
                  onChange={(_, expanded) => handleToggleMessage(item.messageId, expanded)}
                  className="msg-accordion"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    aria-controls={`panel-content-${item.messageId}`}
                    id={`panel-header-${item.messageId}`}
                    className="msg-accordion-summary"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      {/* Bug 80207: name the reassigning user, not the original sender. */}
                      {item.reassignedByUserName ? (
                        <Tooltip
                          arrow
                          title={`${INTERNALMESSAGES.REASSIGNED_BY} ${item.reassignedByUserName} • ${INTERNALMESSAGES.ORIGINAL_SENDER}: ${item.cacheFromName ?? ""}`}
                        >
                          <Typography variant="body2" noWrap>
                            {item.reassignedByUserName}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" noWrap>
                          {item.cacheFromName}
                        </Typography>
                      )}
                      {item.messagePriority === MessagePriority.High && (
                        <PriorityHighIcon
                          sx={{ color: "red", ml: 1, fontSize: 18 }}
                          titleAccess="High"
                        />
                      )}
                      {item.messagePriority === MessagePriority.Low && (
                        <ArrowDownwardIcon sx={{ fontSize: 18 }} titleAccess="Low" />
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Grid2 container spacing={2}>
                      <Grid2 size={{ xs: 12, sm: 7 }}>
                        <Grid2 container>
                          <Grid2 size={1}>
                            <Typography fontWeight="bold" variant="body2">
                              To:
                            </Typography>
                          </Grid2>
                          <Grid2 size={10}>
                            <Typography variant="body2">{item.toCacheName}</Typography>
                          </Grid2>
                        </Grid2>
                        {item.ccCacheName && (
                          <Grid2 container>
                            <Grid2 size={1}>
                              <Typography fontWeight="bold" variant="body2">
                                CC:
                              </Typography>
                            </Grid2>
                            <Grid2 size={10}>
                              <Typography variant="body2">{item.ccCacheName}</Typography>
                            </Grid2>
                          </Grid2>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 5 }} className="msg-time">
                        <Typography variant="body2">
                          {item.sentOn ? format(item.sentOn, GLOBALSETTINGS.DATE_FORMAT_TIME) : ""}
                        </Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Box sx={{ overflowWrap: "break-word" }}>
                          <Typography
                            className="replySubject"
                            component="span"
                            dangerouslySetInnerHTML={{ __html: item.messageBody || "" }}
                          />

                          {/*
                            Bug 80207: show the reassignment note under the original message so the
                            recipient can see who handed it over and why. The memo is plain text
                            typed into a TextField, so it is rendered as text - never as HTML.
                          */}
                          {(item.reassignedByUserName || item.reassignMemo) && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {INTERNALMESSAGES.REASSIGNED_BY} {item.reassignedByUserName}
                                {item.reassignedOn
                                  ? ` • ${format(item.reassignedOn, GLOBALSETTINGS.DATE_FORMAT_TIME)}`
                                  : ""}
                              </Typography>
                              {item.reassignMemo && (
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}
                                >
                                  {item.reassignMemo}
                                </Typography>
                              )}
                            </Alert>
                          )}

                          {item.attachments && item.attachments.length > 0 && (
                            <Grid2 mt={2}>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                gutterBottom
                                mb={3}
                                className="attachments-title"
                              >
                                <AttachFileIcon fontSize="small" />
                                Attachments:
                              </Typography>
                              <Grid2 container spacing={1}>
                                {item.attachments.map((attachment, idx) => {
                                  const fullName = `${attachment.fileName}${attachment.fileType}`;
                                  const displayName = `${fullName} (${formatFileSize(attachment.fileSize || 0)})`;
                                  const fileExt = getFileExtension(attachment.fileName);

                                  return (
                                    <Grid2 key={idx} size={{ xs: 12, md: 4 }}>
                                      <Paper variant="outlined" className="msg=custom-paper">
                                        <List dense disablePadding>
                                          <ListItem
                                            secondaryAction={
                                              <Tooltip title="Download">
                                                <IconButton
                                                  edge="end"
                                                  onClick={() =>
                                                    downloadFile(attachment.attachmentId, fullName)
                                                  }
                                                  size="small"
                                                >
                                                  <FileDownloadOutlinedIcon />
                                                </IconButton>
                                              </Tooltip>
                                            }
                                          >
                                            <ListItemIcon>
                                              {renderSvgIcon(fileExt, "file-icon")}
                                            </ListItemIcon>
                                            <Tooltip title={fullName}>
                                              <ListItemText
                                                primary={displayName}
                                                className="msg-list-item-text"
                                              />
                                            </Tooltip>
                                          </ListItem>
                                        </List>
                                      </Paper>
                                    </Grid2>
                                  );
                                })}
                              </Grid2>
                            </Grid2>
                          )}
                        </Box>
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          })}
        <Box display={displayNew ? "block" : "none"} sx={{ flexGrow: 1, minHeight: 250 }}>
          {validationError && (
            <Alert
              severity="error"
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                px: 2,
              }}
            >
              {validationError}
            </Alert>
          )}
          <QuillWithDrop ref={quillRef} onChange={handleMessageChange} />
        </Box>
      </>
    </MessageDialog>
  );
};

export default ReplyMessage;
